import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync, useDebounced, useErrorMessage } from '../lib/hooks';
import { useI18n, useT } from '../lib/i18n';
import { useToast } from '../components/Toast';
import { Icon } from '../components/Icon';
import { ClientForm } from '../components/ClientForm';
import {
  ActiveBadge, ConfirmDialog, EmptyState, ErrorAlert, LoadingRow, Modal, Pagination, SearchInput,
} from '../components/ui';
import { formatDate } from '@shared/format';
import type { Client } from '@shared/types';

const PER_PAGE = 20;

export function Clients() {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();
  const toMessage = useErrorMessage();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Client | 'new'>();
  const [pendingDelete, setPendingDelete] = useState<Client>();
  const [deleting, setDeleting] = useState(false);

  const q = useDebounced(search);
  const { data, loading, error, reload } = useAsync(
    () => api.clients({ q, page, perPage: PER_PAGE }), [q, page]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.deleteClient(pendingDelete.id);
      toast.success(t('common.deleted'));
      setPendingDelete(undefined);
      reload();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div><h1 className="page-head__title">{t('clients.title')}</h1></div>
        <div className="page-head__actions">
          <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
            <Icon name="plus" />{t('clients.new')}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card__head">
          <div className="toolbar" style={{ flex: 1 }}>
            <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }}
              placeholder={t('clients.searchPlaceholder')} />
            {loading && data && <span className="spinner" />}
          </div>
        </div>

        {error ? <div className="card__body"><ErrorAlert message={error} onRetry={reload} /></div>
          : loading && !data ? <LoadingRow />
          : data && data.items.length === 0 ? (
            <EmptyState
              icon={q ? 'search' : 'users'}
              title={q ? t('common.noResults') : t('clients.empty')}
              hint={q ? t('common.noResultsHint') : t('clients.emptyHint')}
              action={!q && (
                <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
                  <Icon name="plus" />{t('clients.new')}
                </button>
              )}
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('clients.field.name')}</th>
                    <th className="shrink">{t('clients.field.taxId')}</th>
                    <th>{t('clients.field.phone')} · {t('clients.field.email')}</th>
                    <th className="shrink num">{t('clients.invoices')}</th>
                    <th className="shrink">{t('common.status')}</th>
                    <th className="shrink">{t('common.created')}</th>
                    <th className="shrink" />
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((client) => (
                    <tr key={client.id}>
                      <td>
                        <div className="table__primary">{client.name}</div>
                        {client.address && (
                          <div className="table__meta truncate" style={{ maxWidth: 280 }}>
                            {client.address.replace(/\s*\n\s*/g, ', ')}
                          </div>
                        )}
                      </td>
                      <td className="shrink mono">{client.taxId || '—'}</td>
                      <td className="table__meta">
                        <div>{client.phone || '—'}</div>
                        <div className="truncate" style={{ maxWidth: 240 }}>{client.email || ''}</div>
                      </td>
                      <td className="shrink num">{client.invoiceCount ?? 0}</td>
                      <td className="shrink"><ActiveBadge active={client.isActive} /></td>
                      <td className="shrink table__meta nowrap">{formatDate(client.createdAt, locale)}</td>
                      <td className="shrink">
                        <div className="table__actions">
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('common.edit')} aria-label={t('common.edit')}
                            onClick={() => setEditing(client)}>
                            <Icon name="edit" />
                          </button>
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('common.delete')} aria-label={t('common.delete')}
                            onClick={() => setPendingDelete(client)}>
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        {data && data.items.length > 0 && (
          <div className="card__foot">
            <Pagination page={data.page} totalPages={data.totalPages} total={data.total}
              perPage={data.perPage} onChange={setPage} />
          </div>
        )}
      </div>

      {editing && (
        <Modal
          title={editing === 'new' ? t('clients.new') : t('clients.edit')}
          onClose={() => setEditing(undefined)}
        >
          <ClientForm
            client={editing === 'new' ? undefined : editing}
            onCancel={() => setEditing(undefined)}
            onSaved={(_, created) => {
              toast.success(t(created ? 'clients.created' : 'clients.updated'));
              setEditing(undefined);
              reload();
            }}
          />
        </Modal>
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={t('common.delete')}
          message={
            (pendingDelete.invoiceCount ?? 0) > 0
              ? t('clients.deleteBlocked', { name: pendingDelete.name, count: pendingDelete.invoiceCount ?? 0 })
              : t('clients.deleteConfirm', { name: pendingDelete.name })
          }
          confirmLabel={t('common.delete')}
          danger
          busy={deleting}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setPendingDelete(undefined)}
        />
      )}
    </>
  );
}
